import { useEffect, useRef, useState } from "react";
import Cache from "../utils/Cache";

// Matches the "min(Npx, 95vw)" convention already used by several drawers.
const VIEWPORT_MAX_RATIO = 0.95;
const DEFAULT_KEY_STEP = 24;
// One-click "make it big" target for the snap toggle: 75% of the viewport,
// never past the drawer's own content-driven ceiling — a monitor wide enough
// to put 75vw past maxWidth doesn't mean the content has more to show.
const WIDE_SNAP_RATIO = 0.75;
const SNAP_ANIMATION_MS = 220;
// Tolerance for "is the drawer currently at the wide target", so a manual
// drag that lands within a few px of it still reads as "wide" for the
// toggle's direction, instead of requiring exact pixel equality.
const SNAP_EPSILON = 4;

// Rounded here, once, so every caller — pointer drag (sub-pixel clientX),
// keyboard step, and the stored-value restore — deals in whole pixels. Without
// this, a fractional clientX persists as e.g. "937.6065673828125": parseInt
// on restore happens to truncate it back to a clean width, but only by luck.
const clampWidth = (value, min, max) =>
  Math.round(Math.min(Math.max(value, min), max));

// Drag-to-widen state for a right-anchored MUI Drawer. Width can only move up
// from `defaultWidth` (the floor) to `maxWidth` (further capped by 95% of the
// viewport). Persists per drawer TYPE via `storageKey`, mirroring the
// sidebar's Cache-backed collapsed/expanded precedent.
const useResizableDrawerWidth = ({
  storageKey,
  defaultWidth,
  maxWidth = 1200,
  enabled = true,
  step = DEFAULT_KEY_STEP,
}) => {
  const cacheKey = `drawer-width-${storageKey}`;

  const [width, setWidth] = useState(() => {
    const stored = parseInt(Cache.get(cacheKey), 10);
    const effectiveMax = Math.min(
      maxWidth,
      window.innerWidth * VIEWPORT_MAX_RATIO
    );
    return Number.isFinite(stored)
      ? clampWidth(stored, defaultWidth, effectiveMax)
      : defaultWidth;
  });
  const [isDragging, setIsDragging] = useState(false);
  // True only while a snap-toggle transition is running, so the Paper can
  // animate that one change and stay perfectly 1:1 with the pointer the rest
  // of the time — a transition active during a live drag would lag the cursor.
  const [isAnimating, setIsAnimating] = useState(false);

  // Refs, not state: read synchronously inside pointer handlers without
  // waiting on a re-render, and without re-subscribing handlers every drag.
  const dragStartX = useRef(0);
  const dragStartWidth = useRef(width);
  const latestWidthRef = useRef(width);
  const isDraggingRef = useRef(false);

  const handlePointerDown = (event) => {
    if (!enabled) return;
    event.preventDefault(); // no text-selection / native drag ghost
    event.currentTarget.setPointerCapture(event.pointerId);
    dragStartX.current = event.clientX;
    dragStartWidth.current = width;
    latestWidthRef.current = width;
    isDraggingRef.current = true;
    setIsDragging(true);
  };

  // anchor="right" means the handle sits on the drawer's LEFT edge. Moving the
  // pointer left (clientX decreases) must widen the drawer, so delta is
  // measured start-minus-current, not current-minus-start.
  const handlePointerMove = (event) => {
    if (!isDraggingRef.current) return; // guards stray hover pointermoves pre-drag
    const max = Math.min(maxWidth, window.innerWidth * VIEWPORT_MAX_RATIO);
    const deltaX = dragStartX.current - event.clientX;
    const next = clampWidth(dragStartWidth.current + deltaX, defaultWidth, max);
    latestWidthRef.current = next;
    setWidth(next);
  };

  const endDrag = (event) => {
    if (!isDraggingRef.current) return;
    if (event.currentTarget.hasPointerCapture?.(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    isDraggingRef.current = false;
    setIsDragging(false);
    // Persist once, at drag-end — not on every pointermove.
    Cache.set(cacheKey, String(latestWidthRef.current));
  };

  const handleKeyDown = (event) => {
    if (!enabled) return;
    const max = Math.min(maxWidth, window.innerWidth * VIEWPORT_MAX_RATIO);
    let next = null;
    if (event.key === "ArrowLeft")
      next = clampWidth(width + step, defaultWidth, max);
    else if (event.key === "ArrowRight")
      next = clampWidth(width - step, defaultWidth, max);
    else if (event.key === "Home") next = defaultWidth;
    else if (event.key === "End") next = max;
    if (next === null) return;
    event.preventDefault();
    latestWidthRef.current = next;
    setWidth(next);
    Cache.set(cacheKey, String(next)); // each keypress is a discrete, complete action
  };

  // Pointer capture releases itself automatically when the capturing element
  // is removed from the DOM, so an unmount mid-drag needs no cleanup there.
  // The one thing that does need cleanup is this global body-style mutation.
  useEffect(() => {
    if (!isDragging) return undefined;
    const prevCursor = document.body.style.cursor;
    const prevUserSelect = document.body.style.userSelect;
    document.body.style.cursor = "col-resize";
    document.body.style.userSelect = "none";
    return () => {
      document.body.style.cursor = prevCursor;
      document.body.style.userSelect = prevUserSelect;
    };
  }, [isDragging]);

  // isAnimating only needs to outlive the CSS transition itself.
  useEffect(() => {
    if (!isAnimating) return undefined;
    const timer = setTimeout(
      () => setIsAnimating(false),
      SNAP_ANIMATION_MS + 30
    );
    return () => clearTimeout(timer);
  }, [isAnimating]);

  const wideTarget = () =>
    Math.round(
      Math.min(
        window.innerWidth * WIDE_SNAP_RATIO,
        maxWidth,
        window.innerWidth * VIEWPORT_MAX_RATIO
      )
    );

  // Derived from the live width rather than tracked separately: a manual drag
  // after a snap should still make the toggle read (and act) correctly,
  // rather than trusting a flag that drifted out of sync with reality.
  const isWide = width >= wideTarget() - SNAP_EPSILON;

  const toggleWide = () => {
    if (!enabled) return;
    const next = isWide ? defaultWidth : wideTarget();
    setIsAnimating(true);
    latestWidthRef.current = next;
    setWidth(next);
    Cache.set(cacheKey, String(next));
  };

  return {
    width,
    isDragging,
    isAnimating,
    isWide,
    toggleWide,
    handleProps: {
      enabled,
      width,
      minWidth: defaultWidth,
      maxWidth: Math.min(maxWidth, window.innerWidth * VIEWPORT_MAX_RATIO),
      isDragging,
      onPointerDown: handlePointerDown,
      onPointerMove: handlePointerMove,
      onPointerUp: endDrag,
      onPointerCancel: endDrag,
      onKeyDown: handleKeyDown,
    },
  };
};

export default useResizableDrawerWidth;
